import { FC, useMemo } from 'react';
import { css } from '@emotion/react';
import { CallIntelligenceWritebackV1 } from '@frontend/api-call-intel';
import { Divider } from '@frontend/divider';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, Chip, Text } from '@frontend/design-system';
import { generateTrackingId } from '../../utils';
import { formatSyncDate, getSummarySyncChipConfigMap, getSyncErrorMessage } from './utils';

interface SummarySyncPopoverContentProps {
  summarySyncResponse?: CallIntelligenceWritebackV1.Types.CallWritebackStatusIO['output'];
  summarySyncMode?: CallIntelligenceWritebackV1.Types.WritebackSettingMode;
  onRetrySync?: () => void;
  disableRetry?: boolean;
  isDemo?: boolean;
  allowRetrySync?: boolean;
}

const COMPONENT_ID = 'summary-sync-popover';

export const SummarySyncPopoverContent: FC<SummarySyncPopoverContentProps> = ({
  summarySyncResponse,
  onRetrySync,
  isDemo,
  summarySyncMode,
  allowRetrySync,
  disableRetry,
}) => {
  const { t } = useTranslation('call-intelligence');

  const summarySyncStatusConfig = useMemo(
    () => getSummarySyncChipConfigMap({ summarySyncStatus: summarySyncResponse?.status }),
    [summarySyncResponse?.status]
  );

  return (
    <div css={styles.container} data-testid='summary-sync-popover-content'>
      <div css={styles.body}>
        <Text weight='bold'>{t('Call Summary')}</Text>
        <div css={styles.subTitle}>
          <Chip variant={summarySyncStatusConfig.chipVariant} css={{ color: summarySyncStatusConfig.color }}>
            {summarySyncStatusConfig.popoverChipLabel ?? summarySyncStatusConfig.label}
          </Chip>
          {summarySyncResponse?.lastSyncedAt && (
            <Text size='small' color='light'>
              {formatSyncDate(summarySyncResponse.lastSyncedAt)}
            </Text>
          )}
        </div>
        {summarySyncResponse?.status === CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_FAILED && (
          <Text size='small' color='error'>
            {getSyncErrorMessage({
              summarySyncResponse,
              summarySyncMode,
            })}
          </Text>
        )}
      </div>
      {allowRetrySync &&
        summarySyncResponse?.status === CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_FAILED && (
          <>
            <Divider margin={0} />
            <div css={styles.action}>
              <Button
                variant='tertiary'
                iconName='data-sync-small'
                css={styles.actionButton}
                onClick={onRetrySync}
                disabled={disableRetry}
                trackingId={generateTrackingId({
                  component: COMPONENT_ID,
                  context: 'retry-sync-button',
                  isDemoAccount: isDemo,
                })}
              >
                {t('Retry Sync')}
              </Button>
            </div>
          </>
        )}
    </div>
  );
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    max-width: 250px;
  `,
  body: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
    padding: ${theme.spacing(2)};
  `,
  subTitle: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,
  action: css`
    padding: ${theme.spacing(1, 2)};
    display: flex;
    align-items: center;
  `,
  actionButton: css`
    color: ${theme.colors.neutral50};
    svg {
      color: ${theme.colors.neutral50};
    }
  `,
};
